import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { IMG, CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#121212] border border-white/10 focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-white placeholder:text-white/35 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll respond within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero overline="Contact Us" title="Karibu — talk to us" subtitle="Reservations, enquiries, questions — we respond fast, day or night." image={IMG.villaAerial} />
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <FadeUp>
          <h2 className="font-serif-display text-3xl md:text-4xl font-light text-white mb-8">Reach us directly</h2>
          <div className="space-y-6">
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-call-btn">
              <span className="h-12 w-12 border hairline flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-black transition-colors duration-300"><Phone size={18} /></span>
              <div><p className="text-xs uppercase tracking-widest text-white/40">Call Now</p><p className="text-white">{CONTACT.phone}</p></div>
            </a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp-btn">
              <span className="h-12 w-12 border hairline flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-black transition-colors duration-300"><MessageCircle size={18} /></span>
              <div><p className="text-xs uppercase tracking-widest text-white/40">WhatsApp</p><p className="text-white">Chat with us instantly</p></div>
            </a>
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 border hairline flex items-center justify-center text-[#d4af37]"><Mail size={18} /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Email</p>
                {Object.entries(CONTACT.emails).map(([k, v]) => (
                  <a key={k} href={`mailto:${v}`} className="block text-sm text-white/70 hover:text-[#d4af37] transition-colors duration-300" data-testid={`contact-email-${k}`}>{v}</a>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 border hairline flex items-center justify-center text-[#d4af37]"><MapPin size={18} /></span>
              <div><p className="text-xs uppercase tracking-widest text-white/40">Address</p><p className="text-white">{CONTACT.address}</p></div>
            </div>
          </div>
          <div className="mt-10 border hairline overflow-hidden">
            <iframe
              title="Zuri Sana Hotel location"
              src={CONTACT.mapEmbed}
              className="w-full h-64 grayscale contrast-125 opacity-80"
              loading="lazy"
              data-testid="contact-map-iframe"
            />
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <form onSubmit={submit} className="bg-[#121212] border hairline p-8 md:p-12" data-testid="contact-form">
            <h2 className="font-serif-display text-3xl font-light text-white mb-8">Send an enquiry</h2>
            <div className="space-y-4">
              <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} data-testid="contact-email-input" />
              <input value={form.subject} onChange={set("subject")} placeholder="Subject (optional)" className={inputCls} data-testid="contact-subject-input" />
              <textarea required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold mt-6 px-10 py-4 text-[12px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60" data-testid="contact-submit-btn">
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </FadeUp>
      </section>
    </div>
  );
}
